import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  Home, 
  Clock, 
  Calendar, 
  MessageCircleHeart, 
  Settings,
  Plus,
  Search,
  ChevronLeft
} from 'lucide-react';
import { useStore } from '@/store/useStore';

const tabs = [
  { path: '/', icon: Home, label: '首页' },
  { path: '/timeline', icon: Clock, label: '时间轴' },
  { path: '/calendar', icon: Calendar, label: '日历' },
  { path: '/cute-phrases', icon: MessageCircleHeart, label: '童言趣语' },
  { path: '/settings', icon: Settings, label: '设置' },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { children, memories } = useStore();
  const [showAddButton, setShowAddButton] = useState(true);

  const isHome = location.pathname === '/';
  const isAddPage = location.pathname === '/add' || location.pathname.startsWith('/edit');
  const isDetailPage = location.pathname.startsWith('/memory/');
  const isSearchPage = location.pathname === '/search';

  useEffect(() => {
    setShowAddButton(!isAddPage && !isDetailPage);
  }, [isAddPage, isDetailPage]);

  const activeTab = tabs.find(tab => tab.path === location.pathname) || tabs[0];

  // 获取当前选中孩子的记忆数
  const currentChildId = useStore(state => state.currentChildId);
  const currentMemories = currentChildId 
    ? memories.filter(m => m.childId === currentChildId || currentChildId === 'family')
    : memories;

  return (
    <div className="min-h-screen bg-primary-50 flex flex-col max-w-lg mx-auto">
      {/* 顶部导航 */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-primary-100 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          {isHome ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-lg">
                家
              </div>
              <div>
                <h1 className="font-bold text-gray-800">家庭时光</h1>
                <p className="text-xs text-gray-500">{currentMemories.length} 条记录</p>
              </div>
            </div>
          ) : isAddPage ? (
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 rounded-full hover:bg-gray-100"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="font-bold text-gray-800">
                {location.pathname.startsWith('/edit') ? '编辑记录' : '新建记录'}
              </h1>
            </div>
          ) : isDetailPage ? (
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 rounded-full hover:bg-gray-100"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="font-bold text-gray-800">记录详情</h1>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 rounded-full hover:bg-gray-100"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="font-bold text-gray-800">{activeTab.label}</h1>
            </div>
          )}
          
          {/* 搜索按钮 */}
          {(isHome || location.pathname === '/timeline') && (
            <button 
              onClick={() => navigate('/search')}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* 孩子选择器 - 仅在首页显示 */}
        {isHome && (
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => useStore.getState().setCurrentChild(null)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                !currentChildId 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              全部
            </button>
            {children.map(child => (
              <button
                key={child.id}
                onClick={() => useStore.getState().setCurrentChild(child.id)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                  currentChildId === child.id 
                    ? 'text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={currentChildId === child.id ? { backgroundColor: child.color } : undefined}
              >
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: child.color }}
                />
                {child.name}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* 主内容区 */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>

      {/* 底部导航 */}
      {!isAddPage && !isDetailPage && !isSearchPage && (
        <nav className="sticky bottom-0 bg-white border-t border-gray-100 safe-bottom z-40">
          <div className="flex items-center justify-around h-16">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = location.pathname === tab.path;
              return (
                <button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  className={`flex flex-col items-center gap-1 p-2 transition-colors ${
                    isActive ? 'text-primary-500' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'fill-current' : ''}`} />
                  <span className="text-xs">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}

      {/* 浮动添加按钮 */}
      {showAddButton && (
        <button
          onClick={() => navigate('/add')}
          className="fab"
          style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
